// platform/installFunnel.js
//
// The install funnel, measured per device (owner research 2026-11-03, fix 9;
// pack prompt 8 Part C): which browser or in-app view the member came from,
// whether Chrome's install event ever fired, whether `appinstalled` fired,
// whether this boot runs standalone, whether it is the FIRST standalone
// launch on this device, and whether the member was signed in at that first
// launch. Until now "members who reach the installed app signed in, per
// device, per browser" could not be read anywhere.
//
// Once per boot `startInstallFunnel()` builds this boot's record from the
// device (capabilities.js) and what earlier boots left in localStorage, then
// listens for `beforeinstallprompt` (src/index.html captures it as
// `window.promptEvent` and stops its propagation, so the listener is capture-
// phase, as the nudge scheduler's is) and `appinstalled`. The record travels
// as ONE compact header, `X-Poolit-Install-Funnel`, on the profile fetch
// only (useAuth.fetchUserProfile): the backend whitelists three surface
// values in `X-Poolit-Surface`, so a richer record needs its own header,
// which the backend ignores until it reads it (brief
// 2026-11-03-install-funnel-fe-asks). No identity, no IP, nothing a member
// typed: device facts only.
//
// localStorage is per origin and, on an iPhone, NOT copied into the Home
// Screen app (only cookies are, iOS 17.2+), so there the installed app starts
// a record of its own: `promptFired` and `installed` stay 0 and
// `firstStandalone` is 1 on its first open. An Android WebAPK shares Chrome's
// storage and carries the browser-phase facts across. Both are what the
// funnel wants to know. Two known distortions, accepted: an Android uninstall
// and reinstall keeps the storage, so the second install's first launch is
// not a first; "clear site data" wipes it, so the next launch is a first again.
//
// `channel`, `inApp` and `promptFired` are read when the record is read (the
// Telegram globals can land after main.js; Chrome's install event comes after
// a tap). `standalone` and `firstStandalone` are fixed at boot.
//
// Second-lowest layer of src/platform/: imports capabilities.js only. No Vue,
// no store, no axios.
import { inAppKind, installChannel, isStandalone } from './capabilities.js';

/** The header the record rides on, on the profile fetch only. */
export const INSTALL_FUNNEL_HEADER = 'X-Poolit-Install-Funnel';

/** localStorage key of the facts that outlive one boot. */
export const INSTALL_FUNNEL_KEY = 'poolit:install-funnel';

/** This boot's record, once started; null before `startInstallFunnel()`. */
let bootRecord = null;
let stopListening = null;

function readStored() {
  try {
    const raw = localStorage.getItem(INSTALL_FUNNEL_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** @returns {boolean} false when storage is blocked (private mode, quota) */
function writeStored(patch) {
  try {
    localStorage.setItem(INSTALL_FUNNEL_KEY, JSON.stringify({ ...readStored(), ...patch }));
    return true;
  } catch {
    return false;
  }
}

/** The facts that are read live, not fixed at boot. */
function liveFacts() {
  return {
    channel: installChannel(),
    inApp: inAppKind(),
    promptAlready: typeof window !== 'undefined' && Boolean(window.promptEvent),
  };
}

/**
 * Build this boot's record and start listening. Idempotent: the second call
 * returns the same record. `now` is injectable for tests.
 *
 * @returns {{
 *   channel: string, inApp: string|null, promptFired: boolean, installed: boolean,
 *   standalone: boolean, firstStandalone: boolean,
 *   signedInAtFirstStandaloneLaunch: boolean|null,
 * }}
 */
export function startInstallFunnel({ now = Date.now } = {}) {
  if (bootRecord) return bootRecord;

  const stored = readStored();
  const standalone = isStandalone();
  const { channel, inApp, promptAlready } = liveFacts();

  // A first standalone launch the device can remember; with storage blocked
  // every boot would be a first, so none is claimed.
  let firstStandalone = standalone && stored.firstStandaloneAt == null;
  if (firstStandalone) firstStandalone = writeStored({ firstStandaloneAt: now() });
  if (promptAlready && stored.promptFiredAt == null) writeStored({ promptFiredAt: now() });

  bootRecord = {
    channel,
    inApp,
    promptFired: promptAlready || stored.promptFiredAt != null,
    installed: stored.installedAt != null,
    standalone,
    firstStandalone,
    signedInAtFirstStandaloneLaunch:
      typeof stored.signedInAtFirstStandaloneLaunch === 'boolean'
        ? stored.signedInAtFirstStandaloneLaunch
        : null,
  };

  if (typeof window !== 'undefined' && !stopListening) {
    const onPrompt = () => {
      if (!bootRecord.promptFired) {
        bootRecord.promptFired = true;
        writeStored({ promptFiredAt: now() });
      }
    };
    const onInstalled = () => {
      if (!bootRecord.installed) {
        bootRecord.installed = true;
        writeStored({ installedAt: now() });
      }
    };
    window.addEventListener('beforeinstallprompt', onPrompt, { capture: true });
    window.addEventListener('appinstalled', onInstalled);
    stopListening = () => {
      window.removeEventListener('beforeinstallprompt', onPrompt, { capture: true });
      window.removeEventListener('appinstalled', onInstalled);
    };
  }

  return bootRecord;
}

/**
 * The boot's outcome, from auth init: was a member signed in when this boot
 * resolved? Kept once per device, from the first STANDALONE boot that had a
 * verdict (auth init passes no outcome for a boot that ended without one, such
 * as an offline first open), and never overwritten — later boots and later
 * calls leave it, so the funnel keeps the honest first answer. A sign-in
 * inside the app later is a different row: the backend records every
 * signed-in profile fetch. A non-boolean answer records nothing.
 */
export function recordLaunchOutcome({ signedIn } = {}) {
  const record = startInstallFunnel();
  if (typeof signedIn !== 'boolean') return record;
  if (!record.standalone || record.signedInAtFirstStandaloneLaunch !== null) return record;
  record.signedInAtFirstStandaloneLaunch = signedIn;
  writeStored({ signedInAtFirstStandaloneLaunch: signedIn });
  return record;
}

/** A copy of this boot's record (started on demand), the live facts fresh. */
export function installFunnelRecord() {
  const record = startInstallFunnel();
  const { channel, inApp, promptAlready } = liveFacts();
  return { ...record, channel, inApp, promptFired: record.promptFired || promptAlready };
}

const flag = (value) => (value ? '1' : '0');
/** Header-safe: letters, digits and `-` only; anything else is dropped. */
const clean = (value) => String(value ?? '').replace(/[^A-Za-z0-9-]/g, '');

/**
 * The record as the header value: `key=value;key=value`, keys in a fixed
 * order, booleans as 1/0, `inApp` empty for a real browser or an installed
 * app, `signedInAtFirstStandaloneLaunch` absent until known. Header-safe:
 * letters, digits, `-`, `=`, `;` only.
 */
export function installFunnelHeader() {
  const record = installFunnelRecord();
  const pairs = [
    ['channel', clean(record.channel)],
    ['inApp', clean(record.inApp)],
    ['promptFired', flag(record.promptFired)],
    ['installed', flag(record.installed)],
    ['standalone', flag(record.standalone)],
    ['firstStandalone', flag(record.firstStandalone)],
  ];
  if (record.signedInAtFirstStandaloneLaunch !== null) {
    pairs.push(['signedInAtFirstStandaloneLaunch', flag(record.signedInAtFirstStandaloneLaunch)]);
  }
  return pairs.map(([key, value]) => `${key}=${value}`).join(';');
}

/** Tests only: forget this boot (listeners off); storage is left to the test. */
export function resetInstallFunnel() {
  if (stopListening) stopListening();
  stopListening = null;
  bootRecord = null;
}
